import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaFilePdf,
  FaArrowRight,
  FaDownload,
  FaShieldAlt,
  FaBrain,
  FaCogs,
  FaFlask,
  FaChartLine,
  FaNetworkWired,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

const whitepapers = [
  {
    icon: <FaBrain className="text-2xl text-sky-600" />,
    tag: "AI & Manufacturing",
    title: "AI-Driven Quality Management in FDA-Regulated Manufacturing",
    desc:
      "A deep dive into how machine learning models can reduce defect rates, automate visual inspection, and maintain 21 CFR Part 11 compliance — without disrupting validated systems.",
    topics: ["Computer Vision QC", "Deviation Auto-Detection", "CFR Part 11 AI"],
    color: "from-sky-50 to-blue-50",
    border: "border-blue-100",
  },
  {
    icon: <FaShieldAlt className="text-2xl text-emerald-600" />,
    tag: "Compliance & MES",
    title: "Building a Compliance-First MES: A Digital Transformation Playbook",
    desc:
      "Step-by-step guidance for life sciences manufacturers replacing legacy MES platforms while maintaining GMP compliance, audit readiness, and zero downtime during go-live.",
    topics: ["GMP Validation", "CSV Protocols", "6-Week Go-Live Method"],
    color: "from-emerald-50 to-teal-50",
    border: "border-emerald-100",
  },
  {
    icon: <FaCogs className="text-2xl text-violet-600" />,
    tag: "Predictive Maintenance",
    title: "The ROI of Predictive Maintenance in Medical Device Manufacturing",
    desc:
      "Quantifying the financial and operational return of AI-powered predictive maintenance programs — including OEE improvements, unplanned downtime reduction, and regulatory traceability.",
    topics: ["OEE Optimization", "Failure Mode Analysis", "Real-Time Monitoring"],
    color: "from-violet-50 to-purple-50",
    border: "border-violet-100",
  },
  {
    icon: <FaChartLine className="text-2xl text-rose-500" />,
    tag: "Supply Chain",
    title: "SAP IBP for Life Sciences: From Manual Forecasting to Intelligent Planning",
    desc:
      "How leading pharma and med-tech companies are leveraging SAP Integrated Business Planning with LLM-powered analytics to cut lead times by up to 40% and eliminate stockouts.",
    topics: ["SAP IBP", "Demand Sensing", "LLM Analytics"],
    color: "from-rose-50 to-pink-50",
    border: "border-rose-100",
  },
  {
    icon: <FaFlask className="text-2xl text-amber-500" />,
    tag: "Digital Twin",
    title: "Digital Twin Technology in Pharmaceutical Manufacturing",
    desc:
      "Practical frameworks for deploying process digital twins that synchronize OT and IT data in real time — enabling simulation-based batch planning and proactive regulatory reporting.",
    topics: ["OT/IT Integration", "Batch Simulation", "Process Analytics"],
    color: "from-amber-50 to-yellow-50",
    border: "border-amber-100",
  },
  {
    icon: <FaNetworkWired className="text-2xl text-blue-600" />,
    tag: "Data & AI Platform",
    title: "Building the Enterprise AI Data Layer for Regulated Industries",
    desc:
      "Architecture guidance for constructing compliant data lakes, edge analytics pipelines, and LLM fine-tuning workflows that meet FDA, ISO 13485, and GxP data integrity requirements.",
    topics: ["Data Lakes", "Edge Analytics", "GxP Data Integrity"],
    color: "from-blue-50 to-indigo-50",
    border: "border-indigo-100",
  },
];

export default function WhitePapers() {
  const [requested, setRequested] = useState({});

  const handleRequest = (i) => {
    setRequested((prev) => ({ ...prev, [i]: true }));
  };

  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-24 px-6 xl:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Research &amp; Insights
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            White Papers &amp; Technical Guides
          </motion.h1>
          <motion.p
            className="text-blue-200 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Deep-dive research and practitioner playbooks on AI, compliance, and digital
            transformation in regulated manufacturing — written by the XKMINNE team.
          </motion.p>
        </div>
      </section>

      {/* ── White Papers Grid ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">
              Practitioner Research
            </p>
            <h2 className="text-3xl font-bold text-blue-900">
              Download Our Latest White Papers
            </h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm">
              Each paper is authored by XKMINNE practitioners with firsthand experience
              deploying AI and compliance systems at Abbott, AbbVie, and ZOLL Respicardia.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whitepapers.map((paper, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className={`bg-gradient-to-br ${paper.color} border ${paper.border} rounded-2xl p-7 flex flex-col`}
              >
                {/* Icon + Tag */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-white rounded-lg shadow-sm flex-shrink-0">
                    {paper.icon}
                  </div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {paper.tag}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-bold text-gray-900 text-base leading-snug mb-3">
                  {paper.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-grow">
                  {paper.desc}
                </p>

                {/* Topic chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {paper.topics.map((t, j) => (
                    <span
                      key={j}
                      className="text-xs bg-white border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                {requested[i] ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold">
                    <FaFilePdf />
                    Request sent — we'll be in touch!
                  </div>
                ) : (
                  <a
                    href={`mailto:founder@xkminne.com?subject=White%20Paper%20Request%3A%20${encodeURIComponent(paper.title)}`}
                    onClick={() => handleRequest(i)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    <FaDownload className="text-xs" />
                    Request This White Paper
                    <FaArrowRight className="text-xs" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why These Papers ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">
              Written by Practitioners
            </p>
            <h2 className="text-3xl font-bold text-blue-900">
              Research Grounded in Real Deployments
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              {
                stat: "10+",
                label: "Years of Domain Expertise",
                sub: "Across FDA, ISO, SAP, and AI deployments",
              },
              {
                stat: "3",
                label: "Enterprise Client Deployments",
                sub: "Abbott, AbbVie, ZOLL Respicardia",
              },
              {
                stat: "100%",
                label: "FDA Audit Pass Rate",
                sub: "Across all validated system deployments",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-8 rounded-2xl bg-gradient-to-br from-sky-50 to-blue-50 border border-blue-100"
              >
                <div className="text-4xl font-extrabold text-blue-800 mb-2">{item.stat}</div>
                <p className="font-semibold text-gray-800 mb-1">{item.label}</p>
                <p className="text-xs text-gray-500">{item.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-blue-900 text-white text-center px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">
            Want a Custom Briefing for Your Team?
          </h2>
          <p className="text-blue-200 mb-8">
            We offer private technical briefings and executive workshops tailored to your
            industry, regulatory environment, and digital transformation stage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              Request a Briefing <FaArrowRight />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center border border-white/40 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
            >
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 text-center">
        <span className="text-xl font-extrabold text-white">
          XK<span className="text-sky-400">MINNE</span>
        </span>
        <p className="text-sm mt-3 text-gray-500">
          &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
